import { useState, useEffect } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import tw from "twin.macro";

import AnimationRevealPage from "../helpers/AnimationRevealPage";
import Header from "../Components/headers/PageHeader";
import styled, { keyframes } from "styled-components";

/* ---------- Skeleton Animation ---------- */
const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: 200px 0;
  }
`;

/* ---------- Styled Components ---------- */

const Posts = styled.div`
  ${tw`grid grid-cols-1 gap-12 mt-12 sm:grid-cols-2 lg:grid-cols-3`}
`;

const PostContainer = styled.div`
  ${tw`flex flex-col`}
`;

const Post = styled(Link)`
  ${tw`block no-underline`}
`;

const Image = styled.div(({ imageSrc }) => [
  tw`h-80 w-full bg-cover rounded-xl bg-center`,
  imageSrc && { backgroundImage: `url(${imageSrc})` },
]);

const Info = styled.div`
  ${tw`mt-4 text-left flex flex-col`}
`;

const Title = styled.h2`
  ${tw`text-3xl font-semibold text-gray-900 leading-snug mb-2 transition-colors duration-300 no-underline`}
  &:hover {
    ${tw`text-customPurple`}
  }
`;

const CreationDate = styled.div`
  ${tw`text-sm text-gray-500`}
`;

const Content = styled.div`
  ${tw`max-w-screen-4xl mx-auto py-6 px-2 lg:py-10`}
`;

const PaginationContainer = styled.div`
  ${tw`flex justify-center mt-12 space-x-2 items-center`}
`;

const PageButton = styled.button`
  ${tw`px-4 py-2 font-semibold rounded border-0 text-white transition-colors duration-300`}
  ${tw`bg-customPurple hover:bg-customPinkLight`}
  &:disabled {
    ${tw`bg-gray-300 text-black cursor-not-allowed`}
  }
`;

const Ellipsis = styled.span`
  ${tw`px-4 py-2 text-gray-500`}
`;

/* ---------- Skeleton Components ---------- */

const SkeletonBox = styled.div`
  ${tw`rounded-md`}
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 400% 100%;
  animation: ${shimmer} 1.5s infinite;
`;

const SkeletonImage = styled(SkeletonBox)`
  ${tw`h-72 w-full rounded-xl`}
`;

const SkeletonTitle = styled(SkeletonBox)`
  ${tw`h-5 w-3/4 mt-4`}
`;

const SkeletonDate = styled(SkeletonBox)`
  ${tw`h-4 w-1/4 mt-2`}
`;

const SkeletonPost = () => (
  <PostContainer>
    <SkeletonImage />
    <Info>
      <SkeletonTitle />
      <SkeletonDate />
    </Info>
  </PostContainer>
);

/* ---------- Blog Component ---------- */

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchParams, setSearchParams] = useSearchParams();

  const websiteDomain = "milaajbrandset.com";
  const API_URL = process.env.REACT_APP_CMS_API_URL;
  const POSTS_PER_PAGE = 10;

  useEffect(() => {
    let isMounted = true;
    const page = parseInt(searchParams.get("page")) || 1;

    const fetchPosts = async () => {
      try {
        setLoading(true);
        setPosts([]); // clear old posts so skeletons can show

        const response = await axios.get(
          `${API_URL}/api/blog-posts/by-website/${websiteDomain}`,
          {
            params: { depth: 1, draft: false, page, limit: POSTS_PER_PAGE },
            headers: { "Content-Type": "application/json" },
          }
        );

        if (isMounted) {
          setPosts(response.data.blogPosts.docs || []);
          setTotalPages(response.data.blogPosts.totalPages || 1);
          setCurrentPage(page);
          setError(null);
        }
      } catch (err) {
        console.error("API Error:", err);
        if (isMounted) setError("Something went wrong fetching posts.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchPosts();

    return () => {
      isMounted = false;
    };
  }, [websiteDomain, API_URL, searchParams]);

  const handlePageChange = (page) => {
    setSearchParams({ page: page.toString() });
  };

  const renderPagination = () => {
    const maxPagesToShow = 5; // Maximum number of page buttons to show
    const delta = 2; // Number of pages to show before/after current page
    const pages = [];

    if (totalPages <= maxPagesToShow) {
      // Show all pages if totalPages is small
      return Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
        <PageButton
          key={page}
          onClick={() => handlePageChange(page)}
          disabled={page === currentPage}
        >
          {page}
        </PageButton>
      ));
    }

    // Add first page
    pages.push(
      <PageButton
        key={1}
        onClick={() => handlePageChange(1)}
        disabled={1 === currentPage}
      >
        1
      </PageButton>
    );

    // Add ellipsis if currentPage is far from start
    if (currentPage > delta + 2) {
      pages.push(<Ellipsis key="start-ellipsis">...</Ellipsis>);
    }

    // Add middle pages around currentPage
    const startPage = Math.max(2, currentPage - delta);
    const endPage = Math.min(totalPages - 1, currentPage + delta);
    for (let page = startPage; page <= endPage; page++) {
      pages.push(
        <PageButton
          key={page}
          onClick={() => handlePageChange(page)}
          disabled={page === currentPage}
        >
          {page}
        </PageButton>
      );
    }

    // Add ellipsis if currentPage is far from end
    if (currentPage < totalPages - delta - 1) {
      pages.push(<Ellipsis key="end-ellipsis">...</Ellipsis>);
    }

    // Add last page
    if (totalPages > 1) {
      pages.push(
        <PageButton
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          disabled={totalPages === currentPage}
        >
          {totalPages}
        </PageButton>
      );
    }

    return pages;
  };

  if (error)
    return <div className="text-center text-red-600">Error: {error}</div>;

  return (
    <AnimationRevealPage>
      <Header from={"Latest Blogs"} />
      <Content>
        <Posts>
          {loading
            ? Array.from({ length: 6 }).map((_, i) => <SkeletonPost key={i} />)
            : posts.map((post) => (
                <PostContainer key={post.id}>
                  <Post to={`/blog/${post.slug}`}>
                    <Image
                      imageSrc={
                        post.featuredImage?.url ||
                        "https://via.placeholder.com/600x400"
                      }
                    />
                    <Info>
                      <Title>{post.title}</Title>
                      <CreationDate>
                        {post.publishedAt
                          ? new Date(post.publishedAt).toLocaleDateString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              }
                            )
                          : "No date"}
                      </CreationDate>
                    </Info>
                  </Post>
                </PostContainer>
              ))}
        </Posts>

        {!loading && totalPages > 1 && (
          <PaginationContainer>
            <PageButton
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </PageButton>
            {renderPagination()}
            <PageButton
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </PageButton>
          </PaginationContainer>
        )}
      </Content>
    </AnimationRevealPage>
  );
};

export default Blog;